/**
 * Same-origin web auth uses HttpOnly cookies.
 * Cross-site API (www.montestory.in → another host) cannot use those cookies
 * in Safari or private windows, so the SPA keeps the JWTs in sessionStorage
 * and sends Authorization. Never write them to localStorage.
 */
import axios from 'axios';

const LEGACY_TOKEN_KEYS = ['id_token', 'refresh_token'];
const SESSION_ACCESS = 'session_access_token';
const SESSION_REFRESH = 'session_refresh_token';

const CLIENT_SESSION_KEYS = [
  ...LEGACY_TOKEN_KEYS,
  'userDetail',
  'rbac_screens',
  'rbac_permissions',
];

export function purgeLegacyAuthTokens() {
  LEGACY_TOKEN_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      // ignore storage errors (private mode, etc.)
    }
  });
}

export function isCrossSiteApi() {
  const base = process.env.REACT_APP_APIURL || '';
  if (!base || base.startsWith('/')) return false;
  if (typeof window === 'undefined') return false;
  try {
    return new URL(base, window.location.href).host !== window.location.host;
  } catch (_) {
    return false;
  }
}

function writeSession(key, value) {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch (_) {
    // private mode can throw if storage is disabled
  }
}

function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

/** Store JWTs for cross-site calls and strip them before callers persist userDetail. */
export function rememberAuthTokens(data) {
  if (!data || typeof data !== 'object' || !isCrossSiteApi()) return data;
  if (!data.token && !data.refreshToken) return data;
  if (data.token) writeSession(SESSION_ACCESS, data.token);
  if (data.refreshToken) writeSession(SESSION_REFRESH, data.refreshToken);
  const { token, refreshToken, refreshExpiresAt, ...rest } = data;
  return rest;
}

export function getSessionAccessToken() {
  if (!isCrossSiteApi()) return null;
  return readSession(SESSION_ACCESS);
}

export function getSessionRefreshToken() {
  if (!isCrossSiteApi()) return null;
  return readSession(SESSION_REFRESH);
}

export function clearSessionAuthTokens() {
  writeSession(SESSION_ACCESS, '');
  writeSession(SESSION_REFRESH, '');
}

export function applySessionAuthHeader(headers = {}) {
  const next = { ...headers };
  const access = getSessionAccessToken();
  if (access) {
    next.Authorization = `Bearer ${access}`;
  } else {
    delete next.Authorization;
    delete next.authorization;
  }
  return next;
}

export function refreshAuthSession(baseURL) {
  const refreshToken = getSessionRefreshToken();
  const headers = {
    'X-Requested-With': 'XMLHttpRequest',
  };
  const access = getSessionAccessToken();
  if (access) headers.Authorization = `Bearer ${access}`;
  return axios
    .post(
      `${baseURL}/user/refresh-token`,
      refreshToken ? { refreshToken } : {},
      { withCredentials: true, headers },
    )
    .then((res) => {
      if (res?.data) rememberAuthTokens(res.data);
      purgeLegacyAuthTokens();
      return res;
    });
}

export function clearClientSession() {
  CLIENT_SESSION_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      // ignore
    }
  });
  clearSessionAuthTokens();
}
